import { EnergyData, EnergyTag } from '../types/energyData';
import { DashboardDataResponse, SensorDataGroup } from './apiService';

/**
 * Converts API dashboard data response to EnergyData format expected by EnergyDataService
 */
export function convertDashboardDataToEnergyData(
  dashboardData: DashboardDataResponse
): EnergyData[] {
  return dashboardData.sensorData.map((group: SensorDataGroup) => {
    // Create EnergyTag from SensorDataGroup
    const tag: EnergyTag = {
      type: inferTagType(group.tagId),
      id: group.tagId,
      features: inferTagFeatures(group.tagId),
      kind: inferTagKind(group.tagId),
      name: group.label || group.tagId,
      description: group.label || group.tagId,
      rangeMin: inferRangeMin(group.tagId),
      rangeMax: inferRangeMax(group.tagId),
      unit: group.unit || inferUnit(group.tagId),
      status: group.status ? { status: group.status } : undefined,
    };

    // Convert samples
    const samples = group.samples.map((sample) => ({
      utc: sample.utc,
      value: sample.value || 0,
      status: sample.status,
    }));

    return {
      tag,
      samples,
    };
  });
}

/**
 * Infer tag type from tag ID
 */
function inferTagType(tagId: string): string {
  if (tagId.includes('PV')) return 'Solar';
  if (tagId.includes('BT')) return 'Battery';
  if (tagId.includes('EC')) return 'EVCharging';
  if (tagId.includes('HP')) return 'HeatPump';
  if (tagId.includes('EM')) return 'Grid';
  if (tagId.includes('Consumer')) return 'Consumption';
  if (tagId.includes('TZ') || tagId.includes('R')) return 'Building';
  if (tagId.includes('Ambient')) return 'Environmental';
  return 'Unknown';
}

/**
 * Infer tag features from tag ID
 */
function inferTagFeatures(tagId: string): string {
  if (tagId.includes('Power')) return 'Power';
  if (tagId.includes('Energy')) return 'Energy';
  if (tagId.includes('Temp')) return 'Temperature';
  if (tagId.includes('Percent')) return 'Percentage';
  if (tagId.includes('State') || tagId.includes('Status')) return 'Status';
  if (tagId.includes('Pressure')) return 'Pressure';
  if (tagId.includes('Current')) return 'Current';
  if (tagId.includes('Voltage')) return 'Voltage';
  if (tagId.includes('Tariff')) return 'Tariff';
  return 'Unknown';
}

/**
 * Infer tag kind from tag ID
 */
function inferTagKind(tagId: string): 'Input' | 'Schedule' | 'Step' {
  if (tagId.includes('Expected') || tagId.includes('Schedule')) return 'Schedule';
  if (tagId.includes('Step')) return 'Step';
  return 'Input';
}

/**
 * Infer unit from tag ID
 */
function inferUnit(tagId: string): string {
  if (tagId.includes('Power')) return 'W';
  if (tagId.includes('Energy')) return 'kWh';
  if (tagId.includes('Temp')) return '°C';
  if (tagId.includes('Percent')) return '%';
  if (tagId.includes('Pressure')) return 'bar';
  if (tagId.includes('Current')) return 'A';
  if (tagId.includes('Voltage')) return 'V';
  if (tagId.includes('Tariff')) return 'CHF/kWh';
  return '';
}

/**
 * Infer range min from tag ID
 */
function inferRangeMin(tagId: string): number {
  if (tagId.includes('Percent')) return 0;
  if (tagId.includes('Temp')) return -50;
  if (tagId.includes('Power') || tagId.includes('Energy')) return 0;
  return -1000;
}

/**
 * Infer range max from tag ID
 */
function inferRangeMax(tagId: string): number {
  if (tagId.includes('Percent')) return 100;
  if (tagId.includes('Temp')) return 50;
  if (tagId.includes('Power')) return 100000; // 100 kW
  if (tagId.includes('Energy')) return 10000; // 10 MWh
  return 1000;
}






